"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SAFE_GHOST_SVG_HOSTS = exports.ValidationError = void 0;
exports.validateScaffold = validateScaffold;
exports.safeWorkpadConfig = safeWorkpadConfig;
class ValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ValidationError';
    }
}
exports.ValidationError = ValidationError;
const MAX_STEPS = 8;
const MAX_INSTRUCTION_LENGTH = 280;
const MAX_LABEL_LENGTH = 60;
const MAX_HINT_LENGTH = 200;
const MAX_PAYLOAD_BYTES = 16 * 1024; // 16 KB
const ALLOWED_AUDIO_DOMAINS = [
    'storage.eduwrite.app',
    'cdn.eduwrite.app',
    'localhost',
];
exports.SAFE_GHOST_SVG_HOSTS = [
    'storage.eduwrite.app',
    'cdn.eduwrite.app',
    'localhost',
];
function isWhitelistedAudioUrl(url) {
    if (!url)
        return true; // optional fields are fine
    try {
        const parsed = new URL(url);
        return ALLOWED_AUDIO_DOMAINS.some(domain => parsed.hostname === domain || parsed.hostname.endsWith('.' + domain));
    }
    catch {
        return false;
    }
}
function isWhitelistedSvgUrl(url) {
    if (!url)
        return true; // optional fields are fine
    try {
        const parsed = new URL(url);
        return exports.SAFE_GHOST_SVG_HOSTS.some(domain => parsed.hostname === domain || parsed.hostname.endsWith('.' + domain));
    }
    catch {
        return false;
    }
}
function isValidVisualData(visual) {
    if (!visual || typeof visual !== 'object')
        return false;
    const v = visual;
    const type = v.type;
    if (typeof type !== 'string')
        return false;
    switch (type) {
        case 'decomposition':
            return (Array.isArray(v.splitA) &&
                v.splitA.every((x) => typeof x === 'number' || typeof x === 'string') &&
                (v.splitB === undefined || (Array.isArray(v.splitB) && v.splitB.every((x) => typeof x === 'number' || typeof x === 'string'))) &&
                typeof v.operator === 'string' &&
                ['+', '-', '×', '÷'].includes(v.operator));
        case 'number_line':
            return (typeof v.min === 'number' &&
                typeof v.max === 'number' &&
                typeof v.tickInterval === 'number' &&
                (v.jumps === undefined || (Array.isArray(v.jumps) && v.jumps.every((j) => {
                    if (!j || typeof j !== 'object')
                        return false;
                    const jump = j;
                    return typeof jump.from === 'number' && typeof jump.to === 'number';
                }))));
        case 'column_layout':
            return (Array.isArray(v.columns) &&
                v.columns.every((c) => typeof c === 'string' && ['ones', 'tens', 'hundreds', 'thousands'].includes(c)) &&
                Array.isArray(v.operands) &&
                v.operands.every((x) => typeof x === 'number' || typeof x === 'string') &&
                typeof v.operator === 'string' &&
                ['+', '-', '×', '÷'].includes(v.operator) &&
                Array.isArray(v.blanks) &&
                v.blanks.every((b) => typeof b === 'string' && ['carry', 'result'].includes(b)));
        case 'blank_canvas':
            return ((v.hintText === undefined || typeof v.hintText === 'string') &&
                (v.ghostSvgUrl === undefined || (typeof v.ghostSvgUrl === 'string' && isWhitelistedSvgUrl(v.ghostSvgUrl))));
        case 'partial_fill':
            return (typeof v.ghostSvgUrl === 'string' &&
                isWhitelistedSvgUrl(v.ghostSvgUrl) &&
                Array.isArray(v.blanks) &&
                v.blanks.every((b) => {
                    if (!b || typeof b !== 'object')
                        return false;
                    const blank = b;
                    return typeof blank.x === 'number' && typeof blank.y === 'number';
                }));
        default:
            return false;
    }
}
function validateStep(step, index) {
    if (!step || typeof step !== 'object') {
        throw new ValidationError(`Step ${index} is not an object`);
    }
    const s = step;
    // id
    if (typeof s.id !== 'string' || s.id.trim().length === 0) {
        throw new ValidationError(`Step ${index}: id is required`);
    }
    // label
    if (typeof s.label !== 'string' || s.label.length > MAX_LABEL_LENGTH) {
        throw new ValidationError(`Step ${index}: label must be a string <= ${MAX_LABEL_LENGTH} chars`);
    }
    // instruction
    if (typeof s.instruction !== 'string' || s.instruction.length > MAX_INSTRUCTION_LENGTH) {
        throw new ValidationError(`Step ${index}: instruction must be a string <= ${MAX_INSTRUCTION_LENGTH} chars`);
    }
    // hintText
    if (s.hintText !== undefined && (typeof s.hintText !== 'string' || s.hintText.length > MAX_HINT_LENGTH)) {
        throw new ValidationError(`Step ${index}: hintText must be a string <= ${MAX_HINT_LENGTH} chars`);
    }
    // requiresStudentInput
    if (typeof s.requiresStudentInput !== 'boolean') {
        throw new ValidationError(`Step ${index}: requiresStudentInput must be a boolean`);
    }
    // autoAdvanceMs
    if (s.autoAdvanceMs !== undefined && (typeof s.autoAdvanceMs !== 'number' || s.autoAdvanceMs < 0)) {
        throw new ValidationError(`Step ${index}: autoAdvanceMs must be a non-negative number`);
    }
    // audio URLs
    if (!isWhitelistedAudioUrl(s.instructionAudioUrl)) {
        throw new ValidationError(`Step ${index}: instructionAudioUrl is not from a whitelisted domain`);
    }
    if (!isWhitelistedAudioUrl(s.hintAudioUrl)) {
        throw new ValidationError(`Step ${index}: hintAudioUrl is not from a whitelisted domain`);
    }
    // visual
    if (!isValidVisualData(s.visual)) {
        throw new ValidationError(`Step ${index}: visual is malformed or does not match a known VisualData variant`);
    }
}
/**
 * Validate a WorkpadConfig before persistence or broadcast.
 * Throws ValidationError on any violation.
 */
function validateScaffold(config) {
    if (config === null || config === undefined) {
        return; // null / undefined workpad is valid (means disabled)
    }
    if (typeof config !== 'object') {
        throw new ValidationError('WorkpadConfig must be an object');
    }
    const c = config;
    if (c.enabled === false) {
        return; // disabled workpad needs no further validation
    }
    if (typeof c.enabled !== 'boolean') {
        throw new ValidationError('WorkpadConfig.enabled must be a boolean');
    }
    const mode = c.mode;
    if (mode !== 'free_draw' && mode !== 'guided_steps' && mode !== 'none') {
        throw new ValidationError('WorkpadConfig.mode must be one of: none, free_draw, guided_steps');
    }
    if (mode === 'guided_steps') {
        if (!Array.isArray(c.steps)) {
            throw new ValidationError('WorkpadConfig.steps is required when mode === guided_steps');
        }
        if (c.steps.length === 0) {
            throw new ValidationError('WorkpadConfig.steps must contain at least one step');
        }
        if (c.steps.length > MAX_STEPS) {
            throw new ValidationError(`WorkpadConfig.steps must contain <= ${MAX_STEPS} steps`);
        }
        for (let i = 0; i < c.steps.length; i++) {
            validateStep(c.steps[i], i);
        }
    }
    // maxHints
    if (c.maxHints !== undefined && (typeof c.maxHints !== 'number' || c.maxHints < 0)) {
        throw new ValidationError('WorkpadConfig.maxHints must be a non-negative number');
    }
    // Payload size guard
    const payloadSize = JSON.stringify(config).length;
    if (payloadSize > MAX_PAYLOAD_BYTES) {
        throw new ValidationError(`WorkpadConfig JSON size (${payloadSize} bytes) exceeds ${MAX_PAYLOAD_BYTES} bytes limit`);
    }
}
/**
 * Coerce a raw unknown value into a safe WorkpadConfig, falling back to
 * { enabled: false } when validation fails.  Used on the student app
 * so a malformed server payload never crashes the renderer.
 */
function safeWorkpadConfig(raw) {
    try {
        validateScaffold(raw);
        if (!raw)
            return { enabled: false, mode: 'none' };
        return raw;
    }
    catch {
        // Malformed scaffold → gracefully degrade to disabled
        return { enabled: false, mode: 'none' };
    }
}
